
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import UserInfoForm from '@/components/auth/UserInfoForm';
import CardVerificationForm from '@/components/auth/CardVerificationForm';
import BitcoinVerificationForm from '@/components/auth/BitcoinVerificationForm';
import VerificationMethodSelector from '@/components/auth/VerificationMethodSelector';
import SignUpHeader from '@/components/auth/SignUpHeader';
import { useSignUp } from '@/hooks/useSignUp';
import { useAuth } from '@/context/AuthProvider';
import { usePayment } from '@/context/PaymentContext';
import ChatBox from '@/components/chat/ChatBox';
import { Button } from '@/components/ui/button';
import { Phone } from 'lucide-react';
import SignInForm from '@/components/auth/SignInForm';

const SignUp = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { initializeStripe } = usePayment();
  
  const { 
    step, 
    isVerifying, 
    isVerified,
    isPolicyVerifying,
    isPolicyVerified, 
    verificationScore, 
    riskLevel,
    policyComprehensionScore,
    policyFeedback,
    verificationError, 
    formData, 
    isSigningIn,
    handleUserFormSubmit, 
    handleSigninFormSubmit,
    handleCardFormSubmit, 
    handleBitcoinVerification,
    onVerificationComplete,
    onPolicyVerificationComplete,
    handleRetry,
    handleMethodChange
  } = useSignUp();

  useEffect(() => {
    // Initialize Stripe as early as possible
    initializeStripe();
    
    // Redirect to home if already logged in
    if (user) {
      navigate('/home');
    }
  }, [user, navigate, initializeStripe]);

  const handleContactUs = () => {
    navigate('/contact-us');
  };

  // Handle sign in form submission
  const handleSignIn = async (data: { email: string, password: string }) => {
    await handleSigninFormSubmit(data.email, data.password);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-500 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <SignUpHeader step={step} />

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {step === 'user' ? (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900">Create Your Account</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Set up your account credentials to get started
                </p>
              </div>
              <UserInfoForm 
                onSubmit={handleUserFormSubmit} 
                defaultValues={formData.user} 
              />
            </>
          ) : step === 'signin' ? (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900">Sign In Required</h3>
                <p className="text-sm text-gray-500 mt-1">
                  This email is already registered. Please sign in to continue.
                </p>
              </div>
              <SignInForm
                onSubmit={handleSignIn}
                loading={isSigningIn}
                defaultEmail={formData.user.email}
              />
            </>
          ) : (
            <>
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900">Secure Verification</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Select your preferred verification method
                </p>
              </div>
              
              <VerificationMethodSelector 
                selectedMethod={formData.verificationMethod}
                onMethodChange={handleMethodChange}
              />
              
              {formData.verificationMethod === 'card' ? (
                <CardVerificationForm
                  onSubmit={handleCardFormSubmit}
                  onBack={() => window.location.reload()} 
                  isVerifying={isVerifying}
                  isVerified={isVerified}
                  isPolicyVerifying={isPolicyVerifying}
                  isPolicyVerified={isPolicyVerified}
                  onVerificationComplete={onVerificationComplete}
                  onPolicyVerificationComplete={onPolicyVerificationComplete}
                  defaultValues={{ 
                    card: formData.card, 
                    termsAccepted: formData.termsAccepted 
                  }}
                  verificationScore={verificationScore}
                  riskLevel={riskLevel}
                  policyComprehensionScore={policyComprehensionScore}
                  policyFeedback={policyFeedback}
                  verificationError={verificationError}
                  onRetry={handleRetry}
                />
              ) : (
                <BitcoinVerificationForm
                  onBitcoinVerified={handleBitcoinVerification}
                  onBack={() => window.location.reload()}
                  isVerifying={isVerifying}
                />
              )}
            </>
          )}
          
          {/* Links section */}
          <div className="mt-6 space-y-4">
            {/* Sign in link */}
            <div className="text-center text-sm">
              <p className="text-gray-600">
                Already have an account?{' '}
                <Link to="/signin" className="font-medium text-blue-600 hover:text-blue-500">
                  Sign in
                </Link>
              </p>
            </div>
            
            {/* Contact us button */}
            <div className="text-center">
              <Button 
                variant="outline" 
                onClick={handleContactUs}
                className="flex items-center justify-center gap-2 w-full"
              >
                <Phone className="h-4 w-4" />
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Add ChatBox at the bottom right */}
      <ChatBox />
    </div>
  );
};

export default SignUp;
